import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../auth/authSlice";
import RoleGuard from "../../components/RoleGuard";
import { getRoleDisplayName, getRoleBadgeColor } from "../../utils/roleUtils";
import api from "../api";

function NavBar({ currentPage }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  var user =
    useSelector((state) => state.auth.user) ||
    JSON.parse(localStorage.getItem("user") || "null");
  if (typeof user === "string") {
    user = JSON.parse(user);
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    setShowDropdown(false);
    try {
      // Call the API logout endpoint to blacklist the token
      await api.logout();

      // Dispatch logout action to clear Redux state and localStorage
      dispatch(logout());

      // Navigate to login page
      navigate("/login", { replace: true });

      // Show success message
      toast.success("✅ Logged out successfully", {
        autoClose: 3000,
      });
    } catch (error) {
      // Even if API call fails, still clear local storage and redirect
      dispatch(logout());
      navigate("/login", { replace: true });
      toast.error(
        "❌ Logout completed but there was an issue with the server",
        {
          autoClose: 4000,
        }
      );
    }
  };

  // Define a function to determine active styles.
  const activeLinkClass = "border-b-2 border-white";
  const defaultLinkClass =
    "text-white hover:text-gray-300 transition-colors duration-200";

  return (
    <>
      <nav className="bg-black shadow py-4 px-6 flex justify-between items-center min-h-[60px]">
        {/* Left Section */}
        <div className="flex items-center space-x-4 md:space-x-6 overflow-x-auto flex-shrink-0">
          {/* Tribco Logo */}
          <div className="flex items-center">
            <img
              src="/tribco-logo.svg"
              alt="Tribco Inc. Logo"
              className="h-8 w-auto mr-4"
            />
          </div>

          <NavLink
            to="/home"
            className={`text-xl font-bold text-white ${
              currentPage === "/home" ? activeLinkClass : defaultLinkClass
            }`}
          >
            Dashboard
          </NavLink>
          <NavLink
            to="/lead"
            className={`text-white ${
              currentPage === "/lead" ? activeLinkClass : defaultLinkClass
            }`}
          >
            Leads
          </NavLink>
          <NavLink
            to="/unsubmitted-leads"
            className={`text-white ${
              currentPage === "/unsubmitted-leads"
                ? activeLinkClass
                : defaultLinkClass
            }`}
          >
            Unsubmitted Leads
          </NavLink>

          {/* Admin Management - Only show to super admins */}
          <RoleGuard requireSuperAdmin>
            <NavLink
              to="/admin-management"
              className={`text-white font-medium ${
                currentPage === "/admin-management"
                  ? activeLinkClass
                  : defaultLinkClass
              }`}
            >
              Admin Management
            </NavLink>
          </RoleGuard>

          {/* Session Management - Only show to super admins */}
          <RoleGuard requireSuperAdmin>
            <NavLink
              to="/session-management"
              className={`text-white font-medium ${
                currentPage === "/session-management"
                  ? activeLinkClass
                  : defaultLinkClass
              }`}
            >
              Session Management
            </NavLink>
          </RoleGuard>
        </div>

        {/* Right Section */}
        <div className="relative" ref={dropdownRef}>
          <div
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center space-x-2 cursor-pointer hover:bg-gray-800 px-3 py-2 rounded-md transition-colors"
          >
            <div className="flex flex-col items-end">
              <span className="text-white font-medium">{user.name}</span>
              <span
                className={`text-xs px-2 py-1 rounded-full ${getRoleBadgeColor(
                  user.role
                )}`}
              >
                {getRoleDisplayName(user.role)}
              </span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 text-white transition-transform ${
                showDropdown ? "rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
              <button
                onClick={() => {
                  setShowDropdown(false);
                  navigate("/profile");
                }}
                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
              >
                <div className="flex items-center">
                  <svg
                    className="mr-3 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Profile & Sessions
                </div>
              </button>
              <hr className="my-1" />
              <button
                onClick={handleLogout}
                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
              >
                <div className="flex items-center">
                  <svg
                    className="mr-3 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </div>
              </button>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

export default NavBar;
